
import React, { useState } from 'react';
import { X, BookOpen, Play, Terminal, Database, FileText, CheckCircle, Lightbulb, ArrowRight, HelpCircle } from 'lucide-react';
import { MachineCodingTemplate } from '../../data/machine-coding-templates/types';

interface WorkspaceOnboardingProps {
  template: MachineCodingTemplate;
  onClose: () => void;
  onStart: () => void;
}

const WorkspaceOnboarding: React.FC<WorkspaceOnboardingProps> = ({ template, onClose, onStart }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: 'Welcome! 👋',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            You're about to practice <strong className="text-primary-600 dark:text-primary-400">{template.name}</strong>
          </p>
          <div className="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-lg">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <strong>What you'll build:</strong> {template.description}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded">
              <div className="text-xs text-gray-600 dark:text-gray-400">Difficulty</div>
              <div className="font-semibold text-gray-900 dark:text-white capitalize">{template.difficulty}</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded">
              <div className="text-xs text-gray-600 dark:text-gray-400">Time Limit</div>
              <div className="font-semibold text-gray-900 dark:text-white">{template.timeLimit} min</div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Learning Objectives 🎯',
      content: (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            By completing this challenge, you'll learn:
          </p>
          <ul className="space-y-2">
            {template.learningObjectives.map((objective, idx) => (
              <li key={idx} className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300">{objective}</span>
              </li>
            ))}
          </ul>
        </div>
      )
    },
    {
      title: 'Requirements 📋',
      content: (
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Features to Implement:</h4>
            <ul className="space-y-2">
              {template.features.map((feature, idx) => (
                <li key={idx} className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                  <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
          {template.apiEndpoints > 0 ? (
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">API Endpoints:</h4>
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Aim to expose around <strong>{template.apiEndpoints}</strong> API endpoints
                </span>
              </div>
            </div>
          ) : (
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Open-ended build:</h4>
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  This is a blank workspace — design whatever files, modules, and endpoints your solution needs.
                </span>
              </div>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'How to Use 🚀',
      content: (
        <div className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary-600 dark:text-primary-400 font-semibold">1</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Edit Files</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Click files in the left explorer to open and edit them in the Monaco editor (full TypeScript IntelliSense). Add, rename, or delete files inline. Your work autosaves to your account, so it survives a reload.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary-600 dark:text-primary-400 font-semibold">2</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Run Your Code</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Click <strong>"Run"</strong> to execute your code in the in-browser sandbox. If it starts a server (e.g. Express), it boots automatically and <strong>auto-reloads on every save</strong> — no manual restart. Click <strong>"Stop"</strong> to halt it.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary-600 dark:text-primary-400 font-semibold">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Test APIs</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Open the <strong>"API"</strong> tab to send requests to your running server. Use <strong>"Add"</strong> to create your own — the URL auto-targets the live server port, and your saved requests persist across reloads.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary-600 dark:text-primary-400 font-semibold">4</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Manage Packages</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Use the <strong>"Packages"</strong> tab to add or remove npm packages like <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">express</code> or <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">jest</code>. They install on the next Run (or run <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">npm install</code> in the terminal).
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary-600 dark:text-primary-400 font-semibold">5</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Terminal &amp; Output</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Use the bottom panel: a full <strong>Terminal</strong> for commands (<code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">npm install</code>, <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">ls</code>), and the <strong>Output</strong> tab to watch your program's logs. If your project has a test script, <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">npm test</code> runs there too.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary-600 dark:text-primary-400 font-semibold">6</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Download</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Click <strong>"Download"</strong> in the top bar to export your project as a zip (with <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">package.json</code> and <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">tsconfig.json</code>) that opens and runs in your local editor.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Getting Started</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Step {currentStep + 1} of {steps.length}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-gray-200 dark:bg-gray-700">
          <div
            className="h-full bg-primary-600 transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {steps[currentStep].title}
          </h3>
          {steps[currentStep].content}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Skip Tutorial
          </button>
          <div className="flex items-center space-x-2">
            {currentStep > 0 && (
              <button
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Previous
              </button>
            )}
            {currentStep < steps.length - 1 ? (
              <button
                onClick={() => setCurrentStep(prev => prev + 1)}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <span>Next</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={onStart}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <Play className="w-4 h-4" />
                <span>Start Coding!</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceOnboarding;

